import { type ElementType, type PropsWithChildren, forwardRef } from "react";
import classes from "./DDSBodyText.module.css";

export const Variants = [
  "primary",
  "secondary",
  "tertiary",
  "light",
  "disabled",
  "accent",
  "success",
  "warning",
  "danger",
  "plus",
] as const;

export type Variant = (typeof Variants)[number];

export const VariantClasses: Record<Variant, string> = Object.fromEntries(
  Variants.map((v) => [v, classes[v]]),
) as Record<Variant, string>;

export const BodyLevelClasses = {
  bodyLg: classes.bodyLg,
  bodyMd: classes.bodyMd,
  bodySm: classes.bodySm,
} as const;

type DDSBodyTextTagProps = {
  as?: "span" | "p";
  htmlFor?: never;
};

type DDSBodyTextLabelProps = {
  as: "label";
  htmlFor?: string;
};

export type DDSBodyTextProps = (DDSBodyTextTagProps | DDSBodyTextLabelProps) & {
  /**
   * Body size — matches the `--body-{size}-*` CSS tokens.
   *
   * Renamed in 2026 from numeric levels to t-shirt strings: `1`→`"lg"`, `2`→`"md"`,
   * `3`→`"sm"`. See the [type system migration spec](https://linear.app/classdojo/document/type-system-migration-spec-and-codefigma-differences-a635b955ad0a)
   * for the full mapping (Figma may still display legacy "Body/1" style names on stale comps).
   */
  level?: "lg" | "md" | "sm";
  heavy?: boolean;
  className?: string;
  variant?: (typeof Variants)[number];
  id?: string;
  textAlign?: "left" | "center" | "right";
};

const LEVEL_TO_CLASS = {
  lg: "bodyLg",
  md: "bodyMd",
  sm: "bodySm",
} as const;

export const DDSBodyText = forwardRef<HTMLElement, PropsWithChildren<DDSBodyTextProps>>(
  (
    {
      as: tagName = "span",
      level = "lg",
      className,
      heavy,
      variant = "primary",
      textAlign = "left",
      children,
      ...rest
    },
    ref,
  ) => {
    const Tag = tagName as ElementType;
    const classNames = [
      className,
      classes[variant],
      classes[LEVEL_TO_CLASS[level]],
      heavy ? classes.heavy : null,
      textAlign ? classes[textAlign] : null,
    ].filter((c) => !!c);

    return (
      <Tag ref={ref} className={classNames.join(" ")} {...rest}>
        {children}
      </Tag>
    );
  },
);

DDSBodyText.displayName = "DDSBodyText";
